import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import { css } from 'aphrodite';
import * as React from 'react';
import { s } from './styles';

export interface TabToggleItem {
	value: string;
	label: string;
	disabled?: boolean;
}

export interface TabToggleProps
	extends AphroditeStyled<
		Omit<React.ComponentProps<typeof ToggleGroup>, 'value' | 'defaultValue' | 'onValueChange' | 'multiple'>
	> {
	items: TabToggleItem[];
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	/** `small` shrinks the segments for dense chrome; defaults to the standard size. */
	size?: 'small' | 'medium';
	/** `sidebar` renders as a borderless vertical nav, while `segmented` keeps the default pill toggle chrome. */
	variant?: 'segmented' | 'sidebar';
}

export function TabToggle({
	items,
	value,
	defaultValue,
	onValueChange,
	size = 'medium',
	variant = 'segmented',
	orientation: orientationProp,
	styles = [],
	...props
}: TabToggleProps) {
	const orientation = orientationProp ?? (variant === 'sidebar' ? 'vertical' : 'horizontal');
	const handleValueChange = (groupValue: string[]) => {
		if (groupValue.length > 0) {
			onValueChange?.(groupValue[0]);
		}
	};

	return (
		<ToggleGroup
			data-slot='tab-toggle'
			className={css(
				s.root,
				orientation === 'vertical' && s.rootVertical,
				variant === 'sidebar' && s.rootSidebar,
				...styles
			)}
			value={value !== undefined ? [value] : undefined}
			defaultValue={defaultValue !== undefined ? [defaultValue] : undefined}
			onValueChange={handleValueChange}
			multiple={false}
			orientation={orientation}
			{...props}
		>
			{items.map(item => (
				<Toggle
					key={item.value}
					value={item.value}
					disabled={item.disabled}
					className={css(
						s.item,
						orientation === 'vertical' && s.itemVertical,
						size === 'small' && s.itemSmall,
						variant === 'sidebar' && s.itemSidebar
					)}
					data-slot='tab-toggle-item'
				>
					{item.label}
				</Toggle>
			))}
		</ToggleGroup>
	);
}
